import { useState } from 'react';
import { updateUiPanelLayout } from '../../api';
import { usePanelSettings } from '../../contexts/PanelSettingsContext';
import { useToast } from '../../hooks/use-toast';
import { describeLayoutStatus, LAYOUT_TABS, layoutTabTitle } from '../../config/sectionLayouts';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

const describeError = (error) =>
  error?.response?.data?.message || error?.message || 'Неизвестная ошибка';

/**
 * Раскладки вкладок: у каких вкладок своя раскладка и сброс к умолчанию. Сама правка — на
 * вкладке: перетаскивать живые графики нагляднее, чем плашки на схеме.
 */
const LayoutTabsCard = () => {
  const panels = usePanelSettings();
  const { toast } = useToast();
  const [pendingTab, setPendingTab] = useState(null);

  const reset = async (tab) => {
    const layout = panels.layoutFor(tab);
    if (!layout) {
      return;
    }

    setPendingTab(tab);
    try {
      panels.refresh(await updateUiPanelLayout(tab, layout.columns, null, layout.revision));
      // Незавершённая правка этой вкладки после сброса устарела бы: сервер её не примет.
      panels.layoutDrafts.set(tab, null);
      toast({ title: `Вкладке «${panels.tabLabel(tab, layoutTabTitle(tab))}» возвращена раскладка по умолчанию` });
    } catch (error) {
      toast({ title: 'Не удалось сбросить раскладку', description: describeError(error), variant: 'destructive' });
    } finally {
      setPendingTab(null);
    }
  };

  return (
    <Card data-layout-tabs>
      <CardHeader>
        <CardTitle>Раскладка вкладок</CardTitle>
        <CardDescription>
          Где на вкладке стоит каждый график и таблица и какого они размера. Раскладку меняют на
          самой вкладке: над разделом есть кнопка «Настроить раскладку» — её видит только
          администратор и только на экране шире 1280 px. Пользователи видят сохранённую
          раскладку, но изменить её не могут. На телефоне элементы идут одной колонкой в том же
          порядке.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {LAYOUT_TABS.map((tab) => {
          const layout = panels.layoutFor(tab);
          return (
            <div key={tab} data-layout-tab={tab} className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2">
              <span className="min-w-0 flex-1 text-sm font-medium">
                {panels.tabLabel(tab, layoutTabTitle(tab))}
              </span>
              <span className="text-xs text-muted-foreground">{describeLayoutStatus(layout)}</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-ev="settings.interface.layout_reset"
                disabled={!layout || pendingTab === tab}
                onClick={() => reset(tab)}
              >
                Сбросить
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default LayoutTabsCard;
